import { StripeCardElement, StripeCardNumberElement } from "@stripe/stripe-js";
import React from "react";
import type {
  MultilingualFragmentFragment,
  OrderDeliveryInput,
  RenewableFeeEarlyRenewalsQuery,
  RenewableFeeQuoteQuery,
} from "gql/generated";
import {
  BusinessFieldValue,
  CartItem,
  CustomerAddressValue,
  CustomerContact,
  DiscountItem,
  InvoiceAccommodationDraft,
  QuestionnaireResponse,
} from "./cartContextTypes";
import { CartSnapshot } from "./cartSnapshotTypes";

export type CartContextValues = {
  subTotal: number;
  tax: number;
  /** Per-tax-rate breakdown of `tax` (e.g. GST + PST) from the pricing API. */
  taxLines: { name: string; ratePercent?: number | null; amount: number }[];
  grandTotal: number;
  depositTotal: number;
  customerFee: number;
  /** Pickup / ship choice for merch carts; null until the customer picks. */
  delivery: OrderDeliveryInput | null;
  updateDelivery: (delivery: OrderDeliveryInput | null) => void;
  /** Cents, already included in `grandTotal`. */
  shippingTotal: number;
  /** False when the chosen delivery can't be fulfilled (e.g. no zone ships there). */
  deliveryAvailable: boolean | null;
  cart: CartItem[];
  discounts: DiscountItem[];
  customer: CustomerContact;
  /**
   * Stripe's combined `CardElement` (default) or the `CardNumberElement` from
   * the split card form used when a booker makes the postal code optional.
   * Both are accepted by `createPaymentMethod` / `confirmCardPayment`.
   */
  stripeCardElement: StripeCardElement | StripeCardNumberElement | undefined;
  /**
   * ZIP/postal typed into the split card form. Empty when the combined
   * `CardElement` is in use (Stripe collects it inside the iframe).
   */
  cardPostalCode: string;
  paymentMethodId: string;
  acceptedPolicies: boolean;
  questionnaireComplete: boolean;
  questionnaireResponses: QuestionnaireResponse[];
  guestNames: string[];
  eventGuestDetails: { name: string; email: string }[];
  promoCode: string;
  /**
   * True when a promo code was cleared automatically because it no longer
   * applies to the current selection (e.g. the customer changed tiers after
   * applying a tier-scoped code). Consumed by `ValidatePromoCodeForm` to
   * surface an error even though the change wasn't triggered by that form.
   */
  promoCodeError: boolean;
  taxExempt: boolean;
  taxExemptionNumber: string;
  /** True only when the toggle is on AND a valid exemption number is entered — the state that actually zeroes tax. */
  effectiveTaxExempt: boolean;
  updateTaxExemption: (exempt: boolean, exemptionNumber: string) => void;
  /** Customer opted in to automatic renewal charges (renewable fees, card only). */
  autoRenewOptIn: boolean;
  updateAutoRenewOptIn: (optIn: boolean) => void;
  /** Optional note sent with a booking request. */
  messageFromGuest: string;
  updateMessageFromGuest: (message: string) => void;
  addCartItem: (cartItem: CartItem) => void;
  updateCartItem: (productID: string, updatedProps: Partial<CartItem>) => void;
  updateCartItemDetails: (
    productID: string,
    customFieldResponses: CartItem["customFieldResponses"],
    /** Per-business add-on answers; left unchanged when omitted. */
    businessScopes?: CartItem["businessScopes"],
  ) => void;
  removeCartItem: (productID: string) => void;
  updateCustomer: (cust: CustomerContact) => void;
  incompleteCustomer: (cust?: Partial<CustomerContact>) => void;
  updateStripeCardElement: (
    card?: StripeCardElement | StripeCardNumberElement,
  ) => void;
  updateCardPostalCode: (postalCode: string) => void;
  updatePaymentMethodId: (id: string) => void;
  acceptPolicies: () => void;
  updateGuestNames: (guestNames: string[]) => void;
  updateEventGuestDetails: (details: { name: string; email: string }[]) => void;
  resetCartValuesToDefault: () => void;
  updatePromoCode: (code: string) => void;
  completeQuestionnaire: () => void;
  setQuestionnaireCompleteState: (complete: boolean) => void;
  updateQuestionnaireResponses: (
    responses:
      | QuestionnaireResponse[]
      | ((current: QuestionnaireResponse[]) => QuestionnaireResponse[]),
  ) => void;
  selectedBillingInterval: string | null;
  updateBillingInterval: (interval: string) => void;
  selectedTierId: string | null;
  updateSelectedTier: (tierId: string) => void;
  selectedOperatorId: string | null;
  updateOperatorId: (id: string | null) => void;
  businessField: BusinessFieldValue;
  updateBusinessField: (value: BusinessFieldValue) => void;
  customerAddress: CustomerAddressValue;
  updateCustomerAddress: (address: CustomerAddressValue) => void;
  hydrateSnapshot: (snapshot: CartSnapshot) => void;
  /**
   * Invoice admin: register a getter for in-progress dates/guest counts/names so
   * `serializeCart` can persist them without requiring each overlay "Continue".
   */
  setInvoiceAccommodationDraftGetter: (
    getter: (() => InvoiceAccommodationDraft) | null,
  ) => void;
  getInvoiceAccommodationDraft: () => InvoiceAccommodationDraft | null;
  /**
   * Server price for fixed-date pro-rated renewable fees, fetched once the
   * customer is known. `active` is false before then (or for invoice pay),
   * in which case the cart uses the local date-only pro-rate.
   */
  renewableFeeQuote: {
    active: boolean;
    loading: boolean;
    error: boolean;
    data: NonNullable<RenewableFeeQuoteQuery["renewableFeeQuote"]> | null;
  };
  /**
   * Members-only widgets: whether the entered contact details may book.
   * `blocked` stops checkout; the server re-checks when the order is placed.
   * `eligible` is true on widgets open to anyone.
   */
  customerEligibility: {
    active: boolean;
    loading: boolean;
    eligible: boolean;
    blocked: boolean;
    blockedMessage: MultilingualFragmentFragment | null;
  };
  /**
   * Memberships: paid-up memberships this checkout would buy again before
   * their renewal window opens. `entries` covers every business the buyer
   * could pick (or the buyer's own membership when no business is involved);
   * `blocking` is the part of the current selection that stops checkout. The
   * server re-checks when the purchase is made.
   */
  earlyRenewals: {
    loading: boolean;
    entries: EarlyRenewalEntry[];
    blocking: EarlyRenewalEntry[];
  };
};

export type EarlyRenewalEntry = NonNullable<
  RenewableFeeEarlyRenewalsQuery["renewableFeeEarlyRenewals"]
>[number];

export const CartContext = React.createContext<CartContextValues | null>(null);
